import { useCallback } from 'react';
import {
  Department,
  DepartmentFilterInputObject,
  DepartmentsQueryVariables,
  useDepartmentIdsLazyQuery,
  useDepartmentsQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

// NOTE: unlike Asset/Task/etc., `DepartmentFilterInputObject` has no generic
// `id`/`ids` filter by design (BE contract) — "Selected" tab / prefill lookups
// for a known set of department ids must go through the entity-specific
// reverse filter instead (e.g. `departmentTaskGroupsGroupKey`,
// `teamsId`), passed in via `additionalFilters`. The `ids` prop from
// `ListQueryHookProps` is intentionally not supported here.
export default function useDepartmentsQueryHook({
  additionalFilters,
  paginationParams,
  orderingParams,
  searchQuery,
  usePrevious = true,
  skip,
  fetchPolicy = 'cache-and-network',
}: ListQueryHookProps): ListQueryResultWithIds<Department> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );
  const queryVariables: DepartmentsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
  };
  const filters: DepartmentFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'managerFullName' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as DepartmentFilterInputObject[]));
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const query = useDepartmentsQuery({
    fetchPolicy,
    variables: queryVariables,
    skip,
  });
  const [fetchIdsQuery] = useDepartmentIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          onlyIds: true,
          filters: queryVariables.filters,
        },
      }),
    [fetchIdsQuery, queryVariables.filters],
  );
  const { loading, data, previousData, refetch, error } = query;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as Department[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    fetchIds,
    refetch,
    error,
    firstLoading,
    loading,
    collection,
    totalCount,
    totalPages,
  };
}
